
import Vue from 'vue';

import {Subscription, ReplaySubject} from 'rxjs';

import * as Api from '@/api';

import { submittedSubcorpus$ as subcorpus$ } from '@/store/search/streams';
import yieldResultCounts, { CounterInput, CounterOutput } from '@/pages/search/results/TotalsCounterStream';

import * as BLTypes from '@/types/blacklabtypes';

import frac2Percent from '@/mixins/fractionalToPercent';

import Spinner from '@/components/Spinner.vue';

/**
 * Emits update events that contain the new set of totals, so we can update the pagination through our parent components
 * TODO tidy this!
 */

export default Vue.extend({
	components: {Spinner},
	props: {
		initialResults: {
			required: true,
			type: Object as () => BLTypes.BLSearchResult
		},
		type: {
			required: true,
			type: String as () => 'hits'|'docs',
		},
		indexId: {
			required: true,
			type: String as () => string,
		}
	},
	data: () => ({
		subscriptions: [] as Subscription[],

		subcorpus: null as null|BLTypes.BLDocResults, // total searchSpace
		resultCount: {} as CounterOutput, // stream instantly returns output from the initials
		error: null as null|Api.ApiError,

		resultCount$: new ReplaySubject<CounterInput>(1),
	}),

	computed: {
		stats(): BLTypes.BLSearchResult { return this.resultCount.state !== 'error' ? this.resultCount.results : this.initialResults; },
		isCounting(): boolean { return !this.error && this.resultCount.state === 'counting'; },
		isLimited(): boolean { return !this.error && this.resultCount.state === 'limited'; },
		isPaused(): boolean { return !this.error && this.resultCount.state === 'paused'; },
		isFinished(): boolean { return this.isLimited || !(this.isCounting || this.isPaused); },

		resultType(): string { return BLTypes.isHitGroupsOrResults(this.stats) ? this.$t('results.resultsTotals.hits') as string : this.$t('results.resultsTotals.documents') as string; },
		isGroups(): boolean { return BLTypes.isGroups(this.stats); },
		searchTime(): string { return frac2Percent(this.stats.summary.searchTime / 100000, 1).replace('%', 's'); },

		numPrefix(): string { return (this.isLimited || this.isPaused) ? '≥' : ''; },
		numSuffix(): string { return (this.isCounting || this.isPaused) ? '…' : ''; },
		numResults(): number { return BLTypes.isHitGroupsOrResults(this.stats) ? this.stats.summary.numberOfHits : this.stats.summary.numberOfDocs; },
		numResultsRetrieved(): number { return BLTypes.isHitGroupsOrResults(this.stats) ? this.stats.summary.numberOfHitsRetrieved : this.stats.summary.numberOfDocsRetrieved; },
		numGroups(): number { return BLTypes.isGroups(this.stats) ? this.stats.summary.numberOfGroups : 0; },
		// numPages(): number { return Math.ceil((this.isGroups ? this.numGroups : this.numResultsRetrieved) / this.initialResults.summary.searchParam.number); },

		searchSpaceType(): string { return BLTypes.isHitGroupsOrResults(this.stats) ? this.$t('results.resultsTotals.tokens') as string : this.$t('results.resultsTotals.documents') as string; },
		/** The total number of relevant items in the searched subcorpus, tokens if viewing tokens, docs if viewing documents */
		searchSpaceCount(): number {
			if (this.subcorpus == null) {
				return -1;
			}

			return BLTypes.isHitGroupsOrResults(this.stats) ? this.subcorpus.summary.tokensInMatchingDocuments! : this.subcorpus.summary.numberOfDocs;
		},
		percentOfSearchSpaceClarification(): string {
			if (this.subcorpus == null) {
				return '';
			}

			return `Matched ${this.numResults.toLocaleString()} ${this.resultType} in a total of ${this.isLimited ? ' more than' : ''} ${this.searchSpaceCount.toLocaleString()} ${this.searchSpaceType} in the searched subcorpus.`;
		}
	},
	methods: {
		frac2Percent,
		continueCounting() {
			this.error = null;
			this.resultCount$.next({
				indexId: this.indexId,
				operation: this.type,
				results: this.stats
			});
		}
	},
	watch: {
		initialResults: {
			immediate: true,
			handler(v: BLTypes.BLSearchResult) {
				this.error = null;
				this.resultCount$.next({
					indexId: this.indexId,
					operation: this.type,
					results: v
				});
			}
		},

		// todo
		// not immediate on purpose so we don't emit the empty initial object
		resultCount(v: CounterOutput) {
			if (v.state !== 'error') {
				this.$emit('update', v.results);
			}
		}
	},
	created() {
		this.subscriptions.push(
			this.resultCount$
				.pipe(yieldResultCounts)
				.subscribe(v => {
					this.resultCount = v;
					if (v.state === 'error') {
						this.error = v.error;
					} else {
						this.error = null;
					}
				}),
			subcorpus$
				.subscribe(v => this.subcorpus = v)
		);
	},
	destroyed() {
		this.subscriptions.forEach(s => s.unsubscribe());
		this.resultCount$.complete();
	}
});
